import React, {forwardRef} from 'react';
import {useMergeRefs} from '@floating-ui/react';
import {useIsomorphicLayoutEffect} from '@packages/systems/spring-hooks';

import {useModal} from '../ModalProvider';
import {ModalBodyView} from './ModalBody.styles';

import type {HTMLAttributes, SpringTooling} from '../../__SharedTypes';
import type {SizeContextSizeValue} from '@packages/utilities/styled';

export type ModalBodyProps = {
  /**
   * Contents of the Modal body.
   */
  children?: React.ReactNode;
  /**
   * id used for Jest and Cypress test identification.
   */
  ['data-automation-id']?: string;
  /**
   * The size for the ModalBody.
   */
  size?: SizeContextSizeValue;
  /**
   * Whether the ModalBody should have padding.
   */
  isSeamless?: boolean;
} & HTMLAttributes &
  SpringTooling;

/**
 * Creates interaction events and provides the state for whether the
 * top and bottom border should render for ModalBody.
 *
 * Most likely, these interactions will be abstracted to a <Scrollable />
 * component that can handle both x and y scroll rendering.
 */
const useModalScrollableBorders = ({
  shouldShowBottomBorder = false,
  shouldShowTopBorder = false,
  isVisible = false,
}: {
  shouldShowBottomBorder?: boolean;
  shouldShowTopBorder?: boolean;
  isVisible?: boolean;
}) => {
  const [hasTopBorder, setHasTopBorder] = React.useState(false);
  const [hasBottomBorder, setHasBottomBorder] = React.useState(false);
  const modalBodyRef = React.useRef(undefined);

  const setScrollBorders = React.useCallback(() => {
    const node = modalBodyRef.current;
    if (!node) return;

    // @ts-expect-error - TS2339 - Property 'scrollHeight' does not exist on type 'never'. | TS2339 - Property 'clientHeight' does not exist on type 'never'.
    const isElementScrollable = node.scrollHeight > node.clientHeight;
    if (!isElementScrollable) {
      setHasTopBorder(false);
      setHasBottomBorder(false);
    }

    const scrollBuffer = 3;
    // @ts-expect-error - TS2339 - Property 'scrollTop' does not exist on type 'never'.
    const isScrolledFromTop = node.scrollTop - scrollBuffer > 0;
    const isScrolledFromBottom =
      // @ts-expect-error - TS2339 - Property 'scrollTop' does not exist on type 'never'. | TS2339 - Property 'clientHeight' does not exist on type 'never'.
      Math.round(node.scrollTop + node.clientHeight) + scrollBuffer <
      // @ts-expect-error - TS2339 - Property 'scrollHeight' does not exist on type 'never'.
      node.scrollHeight;

    if (shouldShowTopBorder) {
      setHasTopBorder(isScrolledFromTop);
    }
    if (shouldShowBottomBorder) {
      setHasBottomBorder(isScrolledFromBottom);
    }
  }, [shouldShowBottomBorder, shouldShowTopBorder]);

  React.useEffect(setScrollBorders, [isVisible, setScrollBorders]);
  React.useEffect(() => {
    const node = modalBodyRef.current;

    if (!node) return;
    // @ts-expect-error - TS2339 - Property 'addEventListener' does not exist on type 'never'.
    node.addEventListener('scroll', setScrollBorders);
    return () => {
      // @ts-expect-error - TS2339 - Property 'removeEventListener' does not exist on type 'never'.
      node.removeEventListener('scroll', setScrollBorders);
    };
  }, [setScrollBorders]);

  return {
    ref: modalBodyRef,
    hasTopBorder,
    hasBottomBorder,
  };
};

export const ModalBody = forwardRef<HTMLElement, ModalBodyProps>(
  ({children, ...props}, ref) => {
    const {
      dialog,
      hasHeader,
      hasFooter,
      scrollBehavior,
      setModalDescriptionId,
    } = useModal();
    const isVisible = dialog.open;

    const {ref: modalBodyRef} = useModalScrollableBorders({
      shouldShowTopBorder: hasHeader,
      shouldShowBottomBorder: hasFooter,
      isVisible,
    });

    const isScrollable = scrollBehavior === 'inside';
    const mergedRefs = useMergeRefs([modalBodyRef, ref]);

    const id = React.useId();

    useIsomorphicLayoutEffect(() => {
      setModalDescriptionId(id);
      return () => setModalDescriptionId(undefined);
    }, [id, setModalDescriptionId]);

    return (
      <ModalBodyView
        {...props}
        data-modal-body
        id={id}
        isScrollable={isScrollable}
        ref={mergedRefs}
      >
        {children}
      </ModalBodyView>
    );
  }
);

ModalBody.displayName = 'ModalBody';
ModalBody.toString = ModalBodyView.toString;

export default ModalBody;
